import type { BuildingType } from '@kaeliux/shared';

/**
 * Contact-shadow geometry for `BuildingTile` (DS-26). Sprites vary hugely in
 * footprint — a Solar Plant is a small pedestal under a floating cube, a
 * Command Center building is wide and squat, an Engineering Facility is wide and asymmetric
 * with its mass off-centre — so a single fixed-size ellipse can never sit
 * under all of them. Everything here is derived from the tile's *rendered*
 * sprite width (already scaled per-slot by `projectTile`'s `scale`, applied
 * to an ancestor of the shadow element) rather than a pixel constant, so the
 * shadow scales correctly at every depth without this module knowing
 * anything about depth or the stage.
 */

/** One building's shadow tuning, relative to its rendered sprite width. */
export interface ShadowFootprint {
    /** Shadow ellipse width as a fraction of the rendered sprite width. */
    widthFactor: number;
    /**
     * Horizontal shift of the shadow's centre away from the sprite's centre,
     * in px, for sprites whose visual mass sits off to one side (positive =
     * right). Omitted (0) for sprites centred over their own base.
     */
    offsetX?: number;
}

/**
 * Ellipse height as a fraction of its width, fixed so every shadow reads
 * consistently with the ground plane's perspective. `PlotTile` renders its
 * plot outline with `rotateX(52deg)`; this ratio (17/90) is the read that
 * matched that plane before this module existed (DS-15's original
 * 180x34 shadow), carried forward as the shared constant every footprint
 * now scales from instead of each re-deriving its own approximation.
 */
export const SHADOW_HEIGHT_RATIO = 17 / 90;

/**
 * Footprint used for any `BuildingType` with no entry in
 * {@link BUILDING_SHADOW_FOOTPRINTS} below — most buildings' base already
 * fills most of their sprite's width, so this default mirrors DS-15's
 * original single shadow width (180px against a 230px sprite box).
 */
export const DEFAULT_SHADOW_FOOTPRINT: ShadowFootprint = { widthFactor: 0.78 };

/**
 * Per-building overrides, keyed by `BuildingType`. Deliberately sparse —
 * only buildings whose footprint reads wrong under the default belong here.
 * Values are a first pass (issue #222): tuned by eye against the trimmed
 * sprites (commit f609c59) and meant to be revisited from screenshots, not
 * treated as final.
 */
export const BUILDING_SHADOW_FOOTPRINTS: Partial<Record<BuildingType, ShadowFootprint>> = {
    // Small pedestal under a floating energy cube — most of the sprite's
    // width is the cube hanging in the air, not ground contact.
    solarPlant: { widthFactor: 0.32 },
    // Wide, squat, symmetrical — its base is wider than the default ellipse.
    government: { widthFactor: 0.95 },
    // Wide and asymmetric, with an arm assembly held above the ground that
    // must not read as part of the footprint; its base sits right of the
    // sprite's visual centre.
    robotFactory: { widthFactor: 0.85, offsetX: 24 },
};

/** The footprint tuning for a building: its override, or the shared default. */
export function shadowFootprintFor(buildingType: BuildingType): ShadowFootprint {
    return BUILDING_SHADOW_FOOTPRINTS[buildingType] ?? DEFAULT_SHADOW_FOOTPRINT;
}

/** Computed ellipse geometry for one building at a given rendered sprite width. */
export interface ShadowGeometry {
    width: number;
    height: number;
    offsetX: number;
}

/**
 * The shadow ellipse's width/height/offset for `buildingType`, derived from
 * `spriteWidth` (the tile's rendered sprite width, pre-scale — the caller's
 * ancestor transform applies the per-slot depth scale on top of this).
 */
export function computeShadowGeometry(buildingType: BuildingType, spriteWidth: number): ShadowGeometry {
    const footprint = shadowFootprintFor(buildingType);
    const width = spriteWidth * footprint.widthFactor;
    return {
        width,
        height: width * SHADOW_HEIGHT_RATIO,
        offsetX: footprint.offsetX ?? 0,
    };
}

/**
 * The width an `object-contain` sprite actually renders at inside its box.
 *
 * `BuildingTile` caps every sprite at 230x250 while preserving aspect, so a
 * tall sprite renders NARROWER than the box — the trimmed art ranges from
 * 193px to 230px wide, and it varies by level variant too (a Research
 * Research Center's level-1 art is narrower than its level-3 art). Sizing a
 * shadow off the box constant rather than the real width over-widens roughly
 * a third of the sprites, and a per-building factor cannot correct it,
 * because the same building renders at different widths per level and race.
 *
 * Returns `null` when the intrinsic size is not known yet (before the image
 * loads `naturalWidth` is 0), so the caller can fall back to the box width.
 */
export function containedSpriteWidth(
    naturalWidth: number,
    naturalHeight: number,
    boxWidth: number,
    boxHeight: number,
): number | null {
    if (naturalWidth <= 0 || naturalHeight <= 0) return null;
    const scale = Math.min(boxWidth / naturalWidth, boxHeight / naturalHeight, 1);
    return naturalWidth * scale;
}

/**
 * How far the cast shadow is laid over, in degrees of `skewX`.
 *
 * The sprites carry their own baked lighting, and it is consistent: sampling
 * opaque pixels across the building art, the left third is ~41 luminance
 * points brighter than the right third on every building measured. The light
 * is on the LEFT, so the shadow has to fall to the RIGHT. A negative `skewX`
 * does that: points above the bottom origin have a negative offset, so
 * `x' = x + y·tan(-52°)` pushes them right.
 *
 * The angle is a compromise, not the plots' `rotateX(52deg)`: skew displaces
 * a point in proportion to its height, so at 52° the Engineering Facility's raised arms
 * threw a 499px shadow across two neighbouring plots — physically a very low
 * sun, visually a grey smear. 30° keeps the cast within the building's own
 * footprint area while still reading as directional.
 */
export const CAST_SHADOW_SKEW_DEG = -30;

/**
 * Transform for the cast shadow: a copy of the sprite, blackened to its alpha
 * silhouette, laid away from the light with a pure horizontal shear.
 *
 * DS-29 issue #227 round 4: this used to also `scaleY` the silhouette flat
 * (0.34) to lay it down onto the ground, the way a shadow projected onto a
 * steep, top-down plane would foreshorten — matching the plots' own
 * `rotateX(52deg)`. But the building sprites are pre-rendered art baked at a
 * much steeper ~45° isometric camera than the surrounding terrain (ground
 * band, ridges, parallax), which reads as a shallow ~15-20° camera — no
 * single skew/scale combination is a physically consistent projection for
 * both at once (see `CAST_SHADOW_SKEW_DEG`'s own history: even the "real"
 * 52° angle already had to be backed off to avoid a 499px shadow). Dropping
 * the vertical flattening entirely and keeping only the horizontal shear
 * reads better against the shallow-camera terrain than a squashed one did —
 * confirmed by eye before landing this. It's still not a physically
 * "correct" projection; DS-31 is the real fix (re-angling the terrain
 * itself to match the sprites), filed separately as a larger, riskier
 * change than this shadow tweak.
 */
export function castShadowTransform(): string {
    return `translateX(-50%) skewX(${CAST_SHADOW_SKEW_DEG}deg)`;
}
